import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CompanyShell } from "@/components/CompanyShell";
import { UserSearch, Loader2, Database, ShieldCheck, Archive, ChevronRight } from "lucide-react";

const LOC = {
  database: { l: "Profil v databáze", cls: "bg-emerald-50 text-emerald-700 border-emerald-100", icon: Database },
  archived: { l: "Archivovaný profil", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Archive },
  gdpr_shredded: { l: "GDPR – skartované údaje", cls: "bg-red-50 text-red-600 border-red-100", icon: ShieldCheck },
};
const GDPR = { ACTIVE: "Súhlas aktívny", EXPIRING_SOON: "Súhlas čoskoro vyprší", EXPIRED: "Súhlas vypršal", PENDING: "Čaká na súhlas", REVOKED: "Súhlas odvolaný", NONE: "Bez súhlasu" };
const PIPE = { new: "Nový", in_process: "Na schválenie", interview: "Na pohovor", hired: "Prijatý", rejected_retain: "Talent Pool", rejected_shredded: "Zamietnutý" };

export default function SearchCandidate() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get("/company/search", { params: { q } })
        .then((r) => setRes(r.data.results)).catch(() => setRes([])).finally(() => setBusy(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <CompanyShell>
      <h1 className="font-head text-3xl font-bold tracking-tight">Hľadať kandidáta</h1>
      <p className="mt-2 text-slate-600">Overte podľa mena, či kandidát už má vytvorený profil – v databáze aj v GDPR súhlasoch (vrátane skartovaných).</p>
      <div className="mt-6 max-w-xl relative">
        <UserSearch size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="search-candidate-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Meno a priezvisko (min. 2 znaky)…"
          className="w-full rounded-full border border-slate-300 bg-white pl-11 pr-11 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
        {busy && <Loader2 size={16} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-blue-700" />}
      </div>
      <div className="mt-6 max-w-3xl space-y-2" data-testid="search-results">
        {res && res.length === 0 && !busy && (
          <p className="text-sm text-slate-500" data-testid="search-no-results">Kandidát s týmto menom zatiaľ nemá vytvorený profil.</p>
        )}
        {(res || []).map((c) => <ResultRow key={c.id} c={c} onOpen={() => nav(`/company/candidate/${c.id}`)} />)}
      </div>
    </CompanyShell>
  );
}

function ResultRow({ c, onOpen }) {
  const loc = LOC[c.location] || LOC.database;
  return (
    <button data-testid={`search-result-${c.id}`} onClick={onOpen}
      className="w-full flex items-center gap-4 rounded-2xl bg-white border border-slate-200 px-4 py-3 text-left hover:border-blue-300 hover:shadow-sm transition-colors">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 truncate">{c.name}</div>
        <div className="text-xs text-slate-500 truncate">
          {(c.positions || []).join(", ") || (c.areas || []).join(", ") || "Bez pozície"} · {PIPE[c.pipeline_status] || c.pipeline_status}
          {c.created_at && ` · pridaný ${new Date(c.created_at).toLocaleDateString("sk-SK")}`}
        </div>
      </div>
      {c.location === "gdpr_shredded"
        ? <span data-testid={`search-score-${c.id}`} className="hidden sm:inline text-xs text-slate-500">Career Score: <strong>{c.career_score ?? "—"}</strong></span>
        : <span data-testid={`search-gdpr-${c.id}`} className="hidden sm:inline text-xs text-slate-500">{GDPR[c.gdpr_status] || c.gdpr_status}</span>}
      <span data-testid={`search-location-${c.id}`} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${loc.cls}`}>
        <loc.icon size={12} /> {loc.l}
      </span>
      <ChevronRight size={16} className="text-slate-400" />
    </button>
  );
}
